import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, ChevronLeft, ChevronRight, Activity } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

export default function SupportActions() {
  const navigate = useNavigate();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const limit = 50;

  useEffect(() => {
    fetchActions();
  }, [page]);

  const fetchActions = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.get(`${backendUrl}/api/support/actions?page=${page}&limit=${limit}`);
      
      setActions(res.data.actions);
      setTotal(res.data.total);
      setTotalPages(res.data.pages);
    } catch (error) {
      console.error('Erreur chargement actions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionTypeBadge = (actionType) => {
    const configs = {
      account_unlock: { variant: 'default', text: 'Déblocage compte', icon: '🔓' },
      password_reset: { variant: 'default', text: 'Reset password', icon: '🔑' },
      email_verification: { variant: 'default', text: 'Vérification email', icon: '✉️' },
      account_suspension: { variant: 'destructive', text: 'Suspension', icon: '🚫' },
      account_unsuspension: { variant: 'secondary', text: 'Réactivation', icon: '✅' },
      ticket_assigned: { variant: 'secondary', text: 'Ticket assigné', icon: '👤' },
      ticket_resolved: { variant: 'secondary', text: 'Ticket résolu', icon: '✓' },
      user_contacted: { variant: 'outline', text: 'Contact utilisateur', icon: '📞' },
      other: { variant: 'outline', text: 'Autre', icon: '📋' },
    };
    
    const config = configs[actionType] || configs.other;
    return (
      <div className="flex items-center gap-2">
        <span>{config.icon}</span>
        <Badge variant={config.variant}>{config.text}</Badge>
      </div>
    );
  };

  return (
    <div className="space-y-6 p-6" data-testid="support-actions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/support')}
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Journal des Interventions</h1>
            <p className="text-muted-foreground">{total} action{total > 1 ? 's' : ''} enregistrée{total > 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      {/* Actions Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Historique des Actions Support
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : actions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Aucune action enregistrée pour le moment</p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {actions.map((action, idx) => (
                  <div 
                    key={idx} 
                    className="border-l-4 border-primary/30 pl-4 py-3 hover:bg-muted/50 transition-colors rounded-r-lg"
                    data-testid={`action-${idx}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {/* Action Type */}
                        <div className="mb-2">
                          {getActionTypeBadge(action.action_type)}
                        </div>

                        {/* Description */}
                        <p className="font-medium text-sm mb-2">
                          {action.description}
                        </p>

                        {/* Details */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-muted-foreground">
                          <div>
                            <span className="font-semibold">Support :</span>{' '}
                            {action.support_email}
                          </div>
                          <div>
                            <span className="font-semibold">Utilisateur cible :</span>{' '}
                            {action.target_email}
                          </div>
                          <div>
                            <span className="font-semibold">Date :</span>{' '}
                            {format(new Date(action.timestamp), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                          </div>
                        </div>

                        {/* Reason if provided */}
                        {action.reason && (
                          <div className="mt-2 p-2 bg-muted/50 rounded text-xs">
                            <span className="font-semibold">Raison :</span> {action.reason}
                          </div>
                        )}

                        {/* Ticket ID if linked */}
                        {action.ticket_id && (
                          <div className="mt-2">
                            <Badge variant="outline" className="text-xs">
                              Ticket #{action.ticket_id.substring(0, 8)}
                            </Badge>
                          </div>
                        )}
                      </div>

                      {/* Action ID */}
                      <div className="text-xs text-muted-foreground ml-4">
                        ID: {action.id.substring(0, 8)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-6 border-t">
                  <div className="text-sm text-muted-foreground">
                    Page {page} sur {totalPages} • {total} action{total > 1 ? 's' : ''} au total
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                      data-testid="prev-page-button"
                    >
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      Précédent
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === totalPages}
                      onClick={() => setPage(page + 1)}
                      data-testid="next-page-button"
                    >
                      Suivant
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
